import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag as BagIcon, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useStripeConfig } from '../context/StripeConfigContext';
import StripeToggle from './StripeToggle';

const ShoppingBag: React.FC = () => {
  const { items, isCartOpen, closeCart, removeFromCart, updateQuantity, subtotal, checkout, isCheckingOut } = useCart();
  const { mode } = useStripeConfig();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    await checkout(mode);
    closeCart();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          isCartOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[450px] bg-white z-[70] shadow-2xl transform transition-transform duration-500 cubic-bezier(0.16, 1, 0.3, 1) ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 className="font-heading font-bold text-xl uppercase tracking-widest flex items-center gap-2">
              Shopping Bag <span className="text-gray-400 text-sm">({items.length})</span>
            </h2>
            <button onClick={closeCart} className="p-2 hover:bg-gray-100 transition-colors">
              <X size={24} />
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                  <BagIcon size={32} />
                </div>
                <h3 className="font-heading font-bold text-xl uppercase">Nothing claimed yet.</h3>
                <button
                  onClick={() => { closeCart(); navigate('/'); }}
                  className="mt-4 border-b-2 border-black pb-1 font-heading font-bold tracking-widest uppercase hover:text-brand-gold hover:border-brand-gold transition-colors"
                >
                  Enter the archive
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div key={item.uniqueId} className="flex gap-4 animate-fade-in">
                  <div className="w-24 h-32 bg-gray-100 flex-shrink-0">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start">
                        <h4 className="font-heading font-bold text-lg uppercase leading-tight pr-4">{item.title}</h4>
                        <button 
                          onClick={() => removeFromCart(item.uniqueId)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 font-body">
                        {item.category}
                        {item.selectedColor && ` • ${item.selectedColor}`}
                        {item.selectedSize && ` • Size: ${item.selectedSize}`}
                      </p>
                    </div>
                    
                    <div className="flex justify-between items-end">
                      <div className="flex items-center border border-gray-200">
                        <button 
                          onClick={() => updateQuantity(item.uniqueId, -1)}
                          className="p-1.5 hover:bg-gray-50 text-gray-500"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-sm font-heading font-bold">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.uniqueId, 1)}
                          className="p-1.5 hover:bg-gray-50 text-gray-500"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-heading font-bold">£{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="p-6 border-t border-gray-100 bg-gray-50">
              {/* Stripe Mode Toggle - dev only, hidden in production */}
              <div className="mb-4">
                <StripeToggle showInProduction={false} />
              </div>

              <div className="flex justify-between items-center mb-4">
                <span className="font-heading font-bold uppercase tracking-widest text-sm">Subtotal</span>
                <span className="font-heading font-bold text-xl">£{subtotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500 font-body mb-1 text-center">Everything is made when claimed.</p>
              <p className="text-xs text-gray-500 font-body mb-6 text-center">Shipping calculated at checkout.</p>
              <button 
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="w-full bg-brand-black text-white py-4 font-heading font-bold uppercase tracking-[0.2em] hover:bg-brand-gold transition-colors flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingOut ? (
                    'Processing...'
                ) : (
                    <>Checkout <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ShoppingBag;
